import Export16 from '@carbon/icons-react/es/export/16';
import UserData20 from '@carbon/icons-react/es/user--data/20';
import { Tooltip } from 'antd';
import { keyBy } from 'lodash';
import { parse, stringify } from 'query-string';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Breadcrumb } from 'components/atoms/breadcrumb';
import { Button } from 'components/atoms/button';
import { ActionableFilters } from 'components/molecules/filters/actionable-filter';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { UsersGroupedByCompanyComponents } from 'components/organisms/filter-editors';
import { FilterContainer } from 'components/organisms/projects-report/common';
import { CustomRangePicker } from 'components/organisms/projects-report/common/range-picker';
import {
  parseDateRange,
  stringifyDateRange,
} from 'components/organisms/projects-report/common/range-picker/transformer';
import {
  DateRange,
  getRangeObject,
  Range,
} from 'components/organisms/projects-report/common/range-picker/types';
import { FilterableField } from 'components/organisms/tasks/fields';
import { useFilters } from 'components/organisms/tasks/use-filters';
import {
  EmptyState,
  LoadingState,
} from 'components/organisms/users-report/common';
import { UsersReportContent } from 'components/organisms/users-report/content';
import { Argon } from 'components/templates';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useSearch } from 'hooks/use-search';
import { useTranslation } from 'hooks/use-translation';
import { Content } from 'stores/core';
import { useUserFieldsAsFilterableFields } from 'utils/user-fields';
import { from_search, to_query } from '../filter-transformer';
import { RangePickerTrigger } from './range-picker-trigger';
import { useExport } from './use-export';
import { useUsersReport } from './use-users-report';

export enum ProjectsReportView {
  Timelines = 'timelines',
  PhasesAndTasks = 'phases_and_tasks',
}

const RangeOptions = [
  Range.ThisMonth,
  Range.LastMonth,
  Range.ThisQuarter,
  Range.LastQuarter,
  Range.Last90Days,
  Range.Custom,
];
const getSelectEditorComponents = (field) => {
  if (field.fieldName === 'userId') {
    return UsersGroupedByCompanyComponents;
  }

  return undefined;
};

export const UsersReportPage: FC = () => {
  const [search, setSearch] = useSearch<{ range: DateRange }>({
    from: (value) => {
      const range = parseDateRange(
        (value.range ?? '') as string,
        getRangeObject(Range.Last90Days),
      );

      return { range };
    },
    to: (value) => {
      return { range: stringifyDateRange(value.range) };
    },
  });

  const [initialized, setInitialized] = useState(false);
  const [selectedRange, setSelectedRange] = useState<DateRange>(search.range);

  useDeepCompareEffect(() => {
    if (initialized) {
      setSelectedRange(search.range);
    }
  }, [search, initialized]);

  const { fields, isFieldsLoading } = useUserFieldsAsFilterableFields();

  const fields_lookup_by_name = useMemo(
    (): Record<string, FilterableField> =>
      keyBy(isFieldsLoading ? [] : fields, 'fieldName'),
    [fields, isFieldsLoading],
  );
  const { filter, clearFilters, selectFilter, replaceFilter } = useFilters(
    fields_lookup_by_name,
    isFieldsLoading ? 'loading' : 'loaded',
  );

  const [query, setQuery] = useLocalStorage('users_report', '');

  const { search: queryParams } = useLocation();

  useEffect(() => {
    if (!initialized || isFieldsLoading) {
      return;
    }

    setQuery(
      `${to_query(filter)}&${stringify({
        range: stringifyDateRange(selectedRange),
      })}`,
    );
  }, [filter, initialized, isFieldsLoading, selectedRange, query, setQuery]);

  const restoreFilterAndRange = useCallback(
    (restore_query: string) => {
      const saved_filter = from_search(fields_lookup_by_name)(restore_query);
      const saved_range = parseDateRange(
        (parse(restore_query).range ?? '') as string,
        getRangeObject(Range.Last90Days),
      );

      setSearch({ range: saved_range }, true);
      replaceFilter(saved_filter);
      setSelectedRange(saved_range);
    },
    [fields_lookup_by_name, replaceFilter, setSearch],
  );

  useEffect(() => {
    if (initialized || isFieldsLoading) {
      return;
    }
    const restore_query = (queryParams ?? '') === '' ? query : queryParams;

    restoreFilterAndRange(restore_query);
    setInitialized(true);
  }, [
    initialized,
    setInitialized,
    isFieldsLoading,
    fields_lookup_by_name,
    search,
    queryParams,
    replaceFilter,
    setSearch,
    query,
    restoreFilterAndRange,
  ]);

  const usersReport = useUsersReport(selectedRange, filter, initialized);
  const { exportAsCSV } = useExport(selectedRange, filter);

  return (
    <>
      <Helmet>
        <title>{useTranslation('reports.users_performance.title')}</title>
      </Helmet>
      <Argon
        breadcrumbs={
          <Breadcrumb.Item>
            <Link to="/reports">{useTranslation('reports')}</Link>
          </Breadcrumb.Item>
        }
        title={
          <HeaderWithIconTitle
            icon={<UserData20 />}
            title={useTranslation('reports.users_performance.title')}
          />
        }
        actions={{
          primary: (
            <FilterContainer>
              <ActionableFilters
                loading={isFieldsLoading}
                filter={filter}
                fields={fields}
                onChange={selectFilter}
                reset={clearFilters}
                getSelectEditorComponents={getSelectEditorComponents}
              />
              <CustomRangePicker
                range={selectedRange}
                onDateChange={(range) => {
                  setSearch({ range });
                }}
                triggerComponent={<RangePickerTrigger range={selectedRange} />}
                rangeOptions={RangeOptions}
              />
            </FilterContainer>
          ),
          secondary:
            !Content.isLoading(usersReport) && !Content.isEmpty(usersReport) ? (
              <Tooltip
                title={
                  <FormattedMessage id="reports.users_performance.export.tooltip" />
                }
                placement="right"
              >
                <Button
                  icon={<Export16 />}
                  type="text"
                  size="small"
                  disabled={!initialized}
                  onClick={exportAsCSV}
                />
              </Tooltip>
            ) : null,
        }}
        content={
          Content.isLoading(usersReport) ? (
            <LoadingState />
          ) : Content.isEmpty(usersReport) ? (
            <EmptyState />
          ) : (
            <UsersReportContent
              usersReport={usersReport}
              range={selectedRange}
            />
          )
        }
      />
    </>
  );
};
